/**
 * Support workflow & policies — request bodies (see types/support-workflow.ts).
 */
import { z } from 'zod';
import { SUPPORT_CUSTOM_FIELD_TYPES, SUPPORT_FILE_KIND_RULES, SUPPORT_PRIORITY_COLORS, SUPPORT_TICKET_FIELDS, SUPPORT_WORKING_DAY_MODES, } from '../types/support-workflow.js';
/** A target in minutes: up to a year; null = no target. */
const targetMinutes = z.number().int().min(1).max(525_600).nullable().optional();
const optionalText = (max) => z
    .string()
    .max(max)
    .transform((s) => s.trim() || null)
    .nullable()
    .optional();
const clock = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$|^24:00$/, 'Use HH:MM, e.g. 09:00');
/** Policy §12 — PUT /admin/support-workflow/working-calendar. */
export const SupportWorkingCalendarSchema = z.object({
    enabled: z.boolean(),
    timezone: z.string().trim().min(1).max(60),
    days: z
        .array(z
        .object({ mode: z.enum(SUPPORT_WORKING_DAY_MODES), from: clock, to: clock })
        .refine((d) => d.mode === 'off' || d.from < d.to, {
        message: 'A day must end after it starts',
    }))
        .length(7),
    holidays: z.array(z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Use YYYY-MM-DD')).max(366),
});
export const SupportPriorityInputSchema = z.object({
    title: z.string().trim().min(1).max(60),
    description: optionalText(2000),
    cases: optionalText(4000),
    level: z.enum(['low', 'normal', 'high', 'urgent']),
    internalMinutes: targetMinutes,
    externalMinutes: targetMinutes,
    responseMinutes: targetMinutes,
    color: z.enum(SUPPORT_PRIORITY_COLORS).nullable().optional(),
    isDefault: z.boolean().optional(),
});
export const UpdateSupportPrioritySchema = SupportPriorityInputSchema.partial().refine((d) => Object.keys(d).length > 0, { message: 'At least one field must be provided' });
/** Delete a priority; tickets that have it move to `replacementId`. */
export const DeleteSupportPrioritySchema = z.object({
    replacementId: z.string().uuid().optional(),
});
/** The priorities in their new order, top first. */
export const ReorderSupportPrioritiesSchema = z.object({
    ids: z.array(z.string().uuid()).min(1).max(100),
});
export const SupportPriorityIdParamSchema = z.object({ id: z.string().uuid() });
const STATUSES = ['open', 'in_progress', 'waiting_customer', 'resolved', 'closed'];
const BUILTIN_KEYS = SUPPORT_TICKET_FIELDS.map((f) => f.key);
const CUSTOM_KEY = /^custom:[0-9a-f-]{36}$/i;
/** One field a stage needs: a built-in key or "custom:<id>". */
export const SupportStageFieldRuleSchema = z.object({
    fieldKey: z
        .string()
        .refine((k) => BUILTIN_KEYS.includes(k) || CUSTOM_KEY.test(k), 'Unknown field'),
    mode: z.enum(['mandatory', 'optional']),
});
export const SupportWorkflowStageInputSchema = z.object({
    name: z.string().trim().min(1).max(60),
    description: optionalText(2000),
    category: z.enum(STATUSES),
    color: z.enum(SUPPORT_PRIORITY_COLORS).nullable().optional(),
    isStart: z.boolean().optional(),
    collapsed: z.boolean().optional(),
    commercialClosure: z.boolean().optional(),
    isQueue: z.boolean().optional(),
    isApproval: z.boolean().optional(),
    isDropped: z.boolean().optional(),
    agentInstructions: optionalText(8000),
    fields: z
        .array(SupportStageFieldRuleSchema)
        .max(60)
        .refine((list) => new Set(list.map((f) => f.fieldKey)).size === list.length, {
        message: 'A field can be listed once per stage',
    })
        .optional(),
});
export const UpdateSupportWorkflowStageSchema = SupportWorkflowStageInputSchema.partial().refine((d) => Object.keys(d).length > 0, { message: 'At least one field must be provided' });
/** Delete a stage; its tickets move to `replacementId`. */
export const DeleteSupportWorkflowStageSchema = z.object({
    replacementId: z.string().uuid().optional(),
});
/** The stages in their new board order, left first. */
export const ReorderSupportWorkflowStagesSchema = z.object({
    ids: z.array(z.string().uuid()).min(1).max(100),
});
// ─── Custom ticket fields ────────────────────────────────────────────────────
export const SupportCustomFieldInputSchema = z
    .object({
    label: z.string().trim().min(1).max(60),
    description: optionalText(1000),
    type: z.enum(SUPPORT_CUSTOM_FIELD_TYPES),
    options: z.array(z.string().trim().min(1).max(80)).max(50).optional(),
    minFiles: z.number().int().min(1).max(20).nullable().optional(),
    fileKinds: z.enum(SUPPORT_FILE_KIND_RULES).nullable().optional(),
})
    .refine((d) => d.type !== 'select' || (d.options?.length ?? 0) > 0, {
    message: 'A dropdown needs at least one choice',
    path: ['options'],
});
export const UpdateSupportCustomFieldSchema = z
    .object({
    label: z.string().trim().min(1).max(60).optional(),
    description: optionalText(1000),
    options: z.array(z.string().trim().min(1).max(80)).max(50).optional(),
    minFiles: z.number().int().min(1).max(20).nullable().optional(),
    fileKinds: z.enum(SUPPORT_FILE_KIND_RULES).nullable().optional(),
})
    .refine((d) => Object.keys(d).length > 0, { message: 'At least one field must be provided' });
/** A ticket's custom field values; null clears one. Keyed by the field's id. */
export const SupportTicketCustomValuesSchema = z.object({
    values: z.record(z.string().uuid(), z.union([z.string().max(4000), z.number(), z.boolean(), z.null()])),
});
/** GET /support/tickets/:id/requirements?stageId= */
export const SupportTicketRequirementsQuerySchema = z.object({
    stageId: z.string().uuid().optional(),
});
// ─── Multi ticket policy ─────────────────────────────────────────────────────
export const SupportMultiTicketPolicyInputSchema = z.object({
    policy: optionalText(8000),
    maxOpenTickets: z.number().int().min(1).max(100).nullable().optional(),
});
export const SupportContractTypeIdParamSchema = z.object({ contractTypeId: z.string().uuid() });
/**
 * PUT /admin/support-workflow/intake — the whole row, like the working calendar.
 * The pacing fields are optional: a body without them keeps the saved values.
 */
export const SupportIntakeSettingsSchema = z.object({
    requireName: z.boolean(),
    requireEmail: z.boolean(),
    requirePhone: z.boolean(),
    confirmContractId: z.boolean(),
    maxContractIdTries: z.number().int().min(1).max(10),
    replyQuietSeconds: z.number().int().min(0).max(20).optional(),
    maxReplyWaitSeconds: z.number().int().min(5).max(60).optional(),
    typingIndicator: z.boolean().optional(),
});
//# sourceMappingURL=support-workflow.schema.js.map